import { useEffect, useRef, useState } from "react";

import * as service from "../services/userTalentService";
import { getApiError, unwrapProfile } from "../utils/talentUtils";

export function useUserTalent() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const mountedRef = useRef(true);
  const savingRef = useRef(false);
  const requestRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadProfile = async () => {
    const requestId = ++requestRef.current;

    setLoading(true);
    setError("");

    try {
      const response = await service.getMyTalent();

      if (response?.data?.success === false) {
        throw new Error(response.data.message || "Unable to load Talent ID.");
      }

      const nextProfile = unwrapProfile(response);

      if (mountedRef.current && requestId === requestRef.current) {
        setProfile(nextProfile || null);
      }

      return nextProfile;
    } catch (err) {
      if (mountedRef.current && requestId === requestRef.current) {
        setError(getApiError(err, "Unable to load Talent ID."));
      }

      throw err;
    } finally {
      if (mountedRef.current && requestId === requestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadProfile().catch(() => {});
  }, [refreshKey]);

  const refresh = () => {
    setRefreshKey((previous) => previous + 1);
  };

  const execute = async (action, requiresProfile = true) => {
    if (savingRef.current) {
      throw new Error("A Talent ID request is already in progress.");
    }

    if (requiresProfile && !profile?.talentId) {
      const message = "Create your Talent ID first.";
      setError(message);
      throw new Error(message);
    }

    savingRef.current = true;
    setSaving(true);
    setError("");

    try {
      const response = await action();

      if (response?.data?.success === false) {
        throw new Error(response.data.message || "Request failed.");
      }

      await loadProfile();

      return response?.data;
    } catch (err) {
      if (mountedRef.current) {
        setError(getApiError(err, "Unable to save Talent ID."));
      }

      throw err;
    } finally {
      savingRef.current = false;

      if (mountedRef.current) {
        setSaving(false);
      }
    }
  };

  return {
    profile,
    sports: profile?.sports || [],
    experiences: profile?.experiences || [],
    achievements: profile?.achievements || [],
    media: profile?.media || [],

    loading,
    saving,
    error,
    refresh,

    create: (data) => execute(() => service.createTalent(data), false),

    update: (data) =>
      execute(() => service.updateTalent(profile.talentId, data)),

    remove: () => execute(() => service.deleteTalent(profile.talentId)),

    addSport: (data) =>
      execute(() => service.addTalentSport(profile.talentId, data)),

    updateSport: (id, data) =>
      execute(() => service.updateTalentSport(profile.talentId, id, data)),

    removeSport: (id) =>
      execute(() => service.deleteTalentSport(profile.talentId, id)),

    setPrimarySport: (id) =>
      execute(() => service.setPrimaryTalentSport(profile.talentId, id)),

    addExperience: (data) =>
      execute(() => service.addTalentExperience(profile.talentId, data)),

    updateExperience: (id, data) =>
      execute(() => service.updateTalentExperience(profile.talentId, id, data)),

    removeExperience: (id) =>
      execute(() => service.deleteTalentExperience(profile.talentId, id)),

    addAchievement: (data) =>
      execute(() => service.addTalentAchievement(profile.talentId, data)),

    updateAchievement: (id, data) =>
      execute(() =>
        service.updateTalentAchievement(profile.talentId, id, data),
      ),

    removeAchievement: (id) =>
      execute(() => service.deleteTalentAchievement(profile.talentId, id)),

    addMedia: (data) =>
      execute(() => service.addTalentMedia(profile.talentId, data)),

    updateMedia: (id, data) =>
      execute(() => service.updateTalentMedia(profile.talentId, id, data)),

    removeMedia: (id) =>
      execute(() => service.deleteTalentMedia(profile.talentId, id)),
  };
}
